import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

interface BillingLineItem {
  itemId: string;
  category: "labor" | "materials" | "equipment" | "subcontractor" | "change_order" | "other";
  description: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  billingPeriod: string;
  percentComplete: number;
  previouslyBilled: number;
  currentBilling: number;
  retentionRate: number;
  retentionAmount: number;
  notes: string;
}

interface ProjectBillingData {
  invoiceNumber: string;
  jobId: string;
  customerId: string;
  billingPeriod: {
    startDate: string;
    endDate: string;
    description: string;
  };
  invoiceType: "progress" | "completion" | "change_order" | "final" | "retention_release";
  contractInfo: {
    originalContractValue: number;
    approvedChangeOrders: number;
    revisedContractValue: number;
    retentionPercentage: number;
  };
  progressData: {
    overallPercentComplete: number;
    previousPercentComplete: number;
    currentPeriodProgress: number;
    scheduledCompletion: string;
    actualCompletion?: string;
  };
  lineItems: BillingLineItem[];
  billingCalculations: {
    subtotal: number;
    previouslyBilled: number;
    currentPeriodWork: number;
    totalRetention: number;
    currentRetention: number;
    netAmountDue: number;
    salesTax: number;
    totalInvoiceAmount: number;
  };
  paymentTerms: {
    terms: string;
    dueDate: string;
    discountPercent: number;
    discountDays: number;
    lateFeePercent: number;
  };
  compliance: {
    lienWaivers: boolean;
    certifiedPayroll: boolean;
    insuranceCertificates: boolean;
    permitCompliance: boolean;
    safetyCompliance: boolean;
    qualityInspections: boolean;
  };
  supportingDocuments: {
    timesheets?: string[];
    materialReceipts?: string[];
    photos?: string[];
    inspectionReports?: string[];
    changeOrderDocuments?: string[];
    subcontractorInvoices?: string[];
  };
  approvals: {
    projectManager: {
      approved: boolean;
      approvedBy?: string;
      approvalDate?: string;
      comments?: string;
    };
    accounting: {
      approved: boolean;
      approvedBy?: string;
      approvalDate?: string;
      comments?: string;
    };
  };
  notes: string;
  status: "draft" | "pending_review" | "approved" | "sent" | "paid" | "overdue" | "disputed";
}

class ProjectBillingWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: ProjectBillingData = {
    invoiceNumber: "",
    jobId: "",
    customerId: "",
    billingPeriod: {
      startDate: "",
      endDate: "",
      description: "",
    },
    invoiceType: "progress",
    contractInfo: {
      originalContractValue: 0,
      approvedChangeOrders: 0,
      revisedContractValue: 0,
      retentionPercentage: 10,
    },
    progressData: {
      overallPercentComplete: 0,
      previousPercentComplete: 0,
      currentPeriodProgress: 0,
      scheduledCompletion: "",
    },
    lineItems: [],
    billingCalculations: {
      subtotal: 0,
      previouslyBilled: 0,
      currentPeriodWork: 0,
      totalRetention: 0,
      currentRetention: 0,
      netAmountDue: 0,
      salesTax: 0,
      totalInvoiceAmount: 0,
    },
    paymentTerms: {
      terms: "Net 30",
      dueDate: "",
      discountPercent: 2,
      discountDays: 10,
      lateFeePercent: 1.5,
    },
    compliance: {
      lienWaivers: false,
      certifiedPayroll: false,
      insuranceCertificates: false,
      permitCompliance: false,
      safetyCompliance: false,
      qualityInspections: false,
    },
    supportingDocuments: {},
    approvals: {
      projectManager: {
        approved: false,
      },
      accounting: {
        approved: false,
      },
    },
    notes: "",
    status: "draft",
  };

  // Lookup Data
  jobs: any[] = [];
  customers: any[] = [];
  previousInvoices: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.formData.invoiceNumber = this.generateInvoiceNumber();
    this.formData.billingPeriod.startDate = this.getFirstOfMonth();
    this.formData.billingPeriod.endDate = this.getLastOfMonth();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof ProjectBillingData>(
    field: K,
    value: ProjectBillingData[K]
  ) => {
    this.formData[field] = value;
    if (field === "lineItems" || field === "progressData") {
      this.calculateBilling();
    }
    if (field === "jobId") {
      this.loadJobDetails();
    }
  };

  updateBillingPeriod = <K extends keyof ProjectBillingData["billingPeriod"]>(
    field: K,
    value: ProjectBillingData["billingPeriod"][K]
  ) => {
    this.formData.billingPeriod[field] = value;
  };

  updateContractInfo = <K extends keyof ProjectBillingData["contractInfo"]>(
    field: K,
    value: ProjectBillingData["contractInfo"][K]
  ) => {
    this.formData.contractInfo[field] = value;
    this.calculateBilling();
  };

  updateProgressData = <K extends keyof ProjectBillingData["progressData"]>(
    field: K,
    value: ProjectBillingData["progressData"][K]
  ) => {
    this.formData.progressData[field] = value;
    this.calculateBilling();
  };

  updatePaymentTerms = <K extends keyof ProjectBillingData["paymentTerms"]>(
    field: K,
    value: ProjectBillingData["paymentTerms"][K]
  ) => {
    this.formData.paymentTerms[field] = value;
    this.calculateDueDate();
  };

  updateCompliance = <K extends keyof ProjectBillingData["compliance"]>(
    field: K,
    value: ProjectBillingData["compliance"][K]
  ) => {
    this.formData.compliance[field] = value;
  };

  updateApproval = <K extends keyof ProjectBillingData["approvals"]>(
    approver: K,
    value: ProjectBillingData["approvals"][K]
  ) => {
    this.formData.approvals[approver] = value;
  };

  addLineItem = (item: BillingLineItem) => {
    this.formData.lineItems.push(item);
    this.calculateBilling();
  };

  removeLineItem = (index: number) => {
    this.formData.lineItems.splice(index, 1);
    this.calculateBilling();
  };

  updateLineItem = (index: number, item: BillingLineItem) => {
    this.formData.lineItems[index] = item;
    this.calculateBilling();
  };

  generateInvoiceNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
    return `INV-${year}${month}-${random}`;
  };

  generateLineItemId = (): string => {
    return `LI-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  getFirstOfMonth = (): string => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split("T")[0];
  };

  getLastOfMonth = (): string => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split("T")[0];
  };

  loadJobDetails = async () => {
    if (!this.formData.jobId) return;

    try {
      const job = this.jobs.find(j => j.id === this.formData.jobId);
      if (job) {
        this.formData.customerId = job.customerId || "";
        this.formData.contractInfo.originalContractValue = job.contractValue || 0;
        this.formData.contractInfo.revisedContractValue = job.contractValue || 0;
        this.formData.progressData.scheduledCompletion = job.scheduledCompletion || "";

        // Load previous invoices for this job
        this.previousInvoices = [
          {
            id: "1",
            invoiceNumber: "INV-202401-0001",
            amount: 50000,
            percentComplete: 25,
            billingDate: "2024-01-31",
          },
          {
            id: "2",
            invoiceNumber: "INV-202402-0001",
            amount: 75000,
            percentComplete: 50,
            billingDate: "2024-02-29",
          },
        ];

        // Calculate previously billed amount
        const totalPreviouslyBilled = this.previousInvoices.reduce(
          (sum, invoice) => sum + invoice.amount,
          0
        );
        this.formData.billingCalculations.previouslyBilled = totalPreviouslyBilled;

        // Set previous completion percentage
        const lastInvoice = this.previousInvoices[this.previousInvoices.length - 1];
        if (lastInvoice) {
          this.formData.progressData.previousPercentComplete = lastInvoice.percentComplete;
        }
      }
    } catch (error: any) {
      this.error = error.message || "Failed to load job details";
    }
  };

  calculateBilling = () => {
    // Calculate line item totals
    const subtotal = this.formData.lineItems.reduce(
      (sum, item) => sum + item.currentBilling,
      0
    );

    // Calculate current period work based on progress
    const currentPeriodWork = this.formData.contractInfo.revisedContractValue *
      (this.formData.progressData.currentPeriodProgress / 100);

    // Calculate retention
    const currentRetention = currentPeriodWork * (this.formData.contractInfo.retentionPercentage / 100);
    const totalRetention = this.formData.contractInfo.revisedContractValue *
      (this.formData.progressData.overallPercentComplete / 100) *
      (this.formData.contractInfo.retentionPercentage / 100);

    // Calculate net amount due
    const netAmountDue = currentPeriodWork - currentRetention;

    // Calculate sales tax (example: 8.5%)
    const salesTax = netAmountDue * 0.085;

    // Calculate total
    const totalInvoiceAmount = netAmountDue + salesTax;

    this.formData.billingCalculations = {
      subtotal,
      previouslyBilled: this.formData.billingCalculations.previouslyBilled,
      currentPeriodWork,
      totalRetention,
      currentRetention,
      netAmountDue,
      salesTax,
      totalInvoiceAmount,
    };

    this.calculateDueDate();
  };

  calculateDueDate = () => {
    const currentDate = new Date();
    const daysToAdd = this.formData.paymentTerms.terms === "Net 30" ? 30 :
                     this.formData.paymentTerms.terms === "Net 15" ? 15 :
                     this.formData.paymentTerms.terms === "Due on Receipt" ? 0 : 30;

    const dueDate = new Date(currentDate);
    dueDate.setDate(dueDate.getDate() + daysToAdd);
    this.formData.paymentTerms.dueDate = dueDate.toISOString().split("T")[0];
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.jobId) {
          this.error = "Please select a job/project";
          return false;
        }
        if (!this.formData.billingPeriod.startDate) {
          this.error = "Please specify billing period start date";
          return false;
        }
        if (!this.formData.billingPeriod.endDate) {
          this.error = "Please specify billing period end date";
          return false;
        }
        return true;

      case 1:
        if (this.formData.progressData.overallPercentComplete < 0 || this.formData.progressData.overallPercentComplete > 100) {
          this.error = "Overall percent complete must be between 0 and 100";
          return false;
        }
        if (this.formData.progressData.overallPercentComplete < this.formData.progressData.previousPercentComplete) {
          this.error = "Overall percent complete cannot be less than previous period";
          return false;
        }
        return true;

      case 2: {
        if (this.formData.lineItems.length === 0) {
          this.error = "Please add at least one billing line item";
          return false;
        }

        const invalidItems = this.formData.lineItems.some(
          (item) => !item.description || item.quantity <= 0 || item.unitPrice < 0
        );
        if (invalidItems) {
          this.error = "Please complete all line item details";
          return false;
        }
        return true;
      }

      case 3:
        if (this.formData.invoiceType === "final" && !this.allComplianceComplete) {
          this.error = "All compliance items must be completed for final billing";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load jobs
      if (this.rootStore.jobStore) {
        await this.rootStore.jobStore.find({
          query: {
            $limit: 100,
            status: "active",
          },
        });
      }

      // Load customers/accounts
      if (this.rootStore.customersStore) {
        await this.rootStore.customersStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.jobs = this.rootStore.jobStore?.entities || [];
        this.customers = this.rootStore.customersStore?.entities || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitInvoice = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Create invoice record
      const invoiceData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
        status: "pending_review",
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulated API call

      runInAction(() => {
        this.success = "Project invoice submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit invoice";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get selectedJob() {
    return this.jobs.find((j) => j.id === this.formData.jobId);
  }

  get selectedCustomer() {
    return this.customers.find((c) => c.id === this.formData.customerId);
  }

  get allComplianceComplete() {
    return Object.values(this.formData.compliance).every(item => item === true);
  }

  get complianceCompletionRate() {
    const total = Object.keys(this.formData.compliance).length;
    const completed = Object.values(this.formData.compliance).filter(item => item === true).length;
    return Math.round((completed / total) * 100);
  }

  get isReadyForSubmission() {
    return this.formData.approvals.projectManager.approved &&
           this.formData.approvals.accounting.approved &&
           this.allComplianceComplete;
  }

  get previousInvoiceTotal() {
    return this.previousInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  }

  get contractBalanceRemaining() {
    return this.formData.contractInfo.revisedContractValue -
           this.formData.billingCalculations.previouslyBilled -
           this.formData.billingCalculations.currentPeriodWork;
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      invoiceNumber: this.generateInvoiceNumber(),
      jobId: "",
      customerId: "",
      billingPeriod: {
        startDate: this.getFirstOfMonth(),
        endDate: this.getLastOfMonth(),
        description: "",
      },
      invoiceType: "progress",
      contractInfo: {
        originalContractValue: 0,
        approvedChangeOrders: 0,
        revisedContractValue: 0,
        retentionPercentage: 10,
      },
      progressData: {
        overallPercentComplete: 0,
        previousPercentComplete: 0,
        currentPeriodProgress: 0,
        scheduledCompletion: "",
      },
      lineItems: [],
      billingCalculations: {
        subtotal: 0,
        previouslyBilled: 0,
        currentPeriodWork: 0,
        totalRetention: 0,
        currentRetention: 0,
        netAmountDue: 0,
        salesTax: 0,
        totalInvoiceAmount: 0,
      },
      paymentTerms: {
        terms: "Net 30",
        dueDate: "",
        discountPercent: 2,
        discountDays: 10,
        lateFeePercent: 1.5,
      },
      compliance: {
        lienWaivers: false,
        certifiedPayroll: false,
        insuranceCertificates: false,
        permitCompliance: false,
        safetyCompliance: false,
        qualityInspections: false,
      },
      supportingDocuments: {},
      approvals: {
        projectManager: {
          approved: false,
        },
        accounting: {
          approved: false,
        },
      },
      notes: "",
      status: "draft",
    };
    this.previousInvoices = [];
  };
}

export default ProjectBillingWizardStore;